import { useEffect, useRef, useState } from "react";
import { Phone, Video, MessageSquare, X, Minus, Maximize2, Mic, MicOff, VideoOff, PhoneOff, Send, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { conversationsApi, ApiError } from "@/lib/api";
import { toast } from "sonner";

export type CommsMode = "video" | "voice" | "chat";

export interface CommsPeer {
  name: string;
  initials: string;
  role?: string;
  meta?: string;
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  peer: CommsPeer;
  initialMode?: CommsMode;
  /** Real conversation id — when present, Chat tab is backend-wired (load/send via /conversations). Omit for an unavailable counterpart (no account). */
  conversationId?: string | null;
  token?: string | null;
}

export function CommsPanel({ open, onOpenChange, peer, initialMode = "chat", conversationId = null, token }: Props) {
  const [mode, setMode] = useState<CommsMode>(initialMode);
  const [collapsed, setCollapsed] = useState(false);
  const [muted, setMuted] = useState(false);
  const [camOff, setCamOff] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [messages, setMessages] = useState<{ id?: string; from: "me" | "them"; text: string; at: string }[]>([]);
  const [draft, setDraft] = useState("");
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setMode(initialMode);
    setElapsed(0);
    setCollapsed(false);
  }, [open, initialMode]);

  useEffect(() => {
    if (!open || collapsed || mode === "chat") return;
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [open, collapsed, mode]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, mode, collapsed]);

  useEffect(() => {
    if (!open || !conversationId) {
      setMessages([]);
      return;
    }
    let cancelled = false;
    setLoadingMessages(true);
    conversationsApi
      .messages(conversationId, token)
      .then((list) => {
        if (cancelled) return;
        setMessages(
          list.map((m) => ({
            id: m.id,
            from: m.sender === "me" ? "me" : "them",
            text: m.body,
            at: new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          }))
        );
      })
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Could not load messages"))
      .finally(() => !cancelled && setLoadingMessages(false));
    return () => {
      cancelled = true;
    };
  }, [open, conversationId, token]);

  if (!open) return null;

  const mmss = `${String(Math.floor(elapsed / 60)).padStart(2, "0")}:${String(elapsed % 60).padStart(2, "0")}`;

  const send = async () => {
    const t = draft.trim();
    if (!t || sending) return;
    if (!conversationId) {
      toast.error("No account on file for this contact yet — chat isn't available.");
      return;
    }
    setSending(true);
    setDraft("");
    try {
      const created = await conversationsApi.postMessage(conversationId, t, token);
      setMessages((m) => [
        ...m,
        { id: created.id, from: "me", text: created.body, at: new Date(created.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) },
      ]);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not send message");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className={cn(
        "fixed bottom-4 right-4 z-50 w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border bg-surface shadow-2xl transition-all",
        collapsed ? "h-14" : "h-[520px] max-h-[calc(100vh-2rem)]"
      )}
      role="dialog"
      aria-label={`Comms with ${peer.name}`}
    >
      {/* Header */}
      <div className="flex h-14 items-center justify-between gap-2 border-b bg-gradient-to-r from-primary to-primary/85 px-3 text-primary-foreground">
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-foreground/15 text-sm font-bold">
            {peer.initials}
            <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-success ring-2 ring-primary" />
          </div>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold">{peer.name}</div>
            <div className="truncate text-[11px] text-primary-foreground/70">
              {mode === "chat" ? peer.role ?? "Online" : `${mode === "video" ? "Video" : "Voice"} call · ${mmss}`}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setCollapsed((v) => !v)}
            className="rounded-md p-1.5 text-primary-foreground/80 hover:bg-primary-foreground/10 hover:text-primary-foreground"
            aria-label={collapsed ? "Expand" : "Collapse"}
          >
            {collapsed ? <Maximize2 className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
          </button>
          <button
            onClick={() => onOpenChange(false)}
            className="rounded-md p-1.5 text-primary-foreground/80 hover:bg-primary-foreground/10 hover:text-primary-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {!collapsed ? (
        <>
          {/* Mode tabs */}
          <div className="flex border-b bg-surface-muted">
            {([
              { id: "video" as const, icon: Video, label: "Video" },
              { id: "voice" as const, icon: Phone, label: "Voice" },
              { id: "chat" as const, icon: MessageSquare, label: "Chat" },
            ]).map((t) => {
              const active = mode === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setMode(t.id)}
                  className={cn(
                    "flex flex-1 items-center justify-center gap-1.5 border-b-2 py-2.5 text-xs font-medium transition-colors",
                    active
                      ? "border-primary text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  )}
                >
                  <t.icon className="h-3.5 w-3.5" /> {t.label}
                </button>
              );
            })}
          </div>

          {/* Body */}
          <div className="flex h-[calc(520px-56px-41px)] flex-col">
            {mode === "video" ? (
              <VideoPane peer={peer} camOff={camOff} muted={muted} onToggleCam={() => setCamOff((v) => !v)} onToggleMute={() => setMuted((v) => !v)} onEnd={() => onOpenChange(false)} />
            ) : mode === "voice" ? (
              <VoicePane peer={peer} muted={muted} onToggleMute={() => setMuted((v) => !v)} onEnd={() => onOpenChange(false)} elapsed={mmss} />
            ) : (
              <div className="flex flex-1 flex-col">
                <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto p-3">
                  {!conversationId ? (
                    <div className="mt-8 text-center text-xs text-muted-foreground">
                      No account on file for {peer.name} yet — chat isn't available.
                    </div>
                  ) : loadingMessages ? (
                    <div className="mt-8 text-center text-xs text-muted-foreground">Loading…</div>
                  ) : messages.length === 0 ? (
                    <div className="mt-8 text-center text-xs text-muted-foreground">Start the conversation — messages are tied to this thread.</div>
                  ) : null}
                  {messages.map((m, i) => (
                    <div key={m.id ?? i} className={cn("flex", m.from === "me" ? "justify-end" : "justify-start")}>
                      <div
                        className={cn(
                          "max-w-[75%] rounded-2xl px-3 py-2 text-sm",
                          m.from === "me"
                            ? "rounded-br-sm bg-primary text-primary-foreground"
                            : "rounded-bl-sm bg-muted text-foreground"
                        )}
                      >
                        <div>{m.text}</div>
                        <div className={cn("mt-0.5 text-[10px]", m.from === "me" ? "text-primary-foreground/70" : "text-muted-foreground")}>{m.at}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    send();
                  }}
                  className="flex items-center gap-2 border-t p-2"
                >
                  <Input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder={conversationId ? "Type a message…" : "No account on file"}
                    className="h-9"
                    aria-label="Message"
                    disabled={!conversationId || sending}
                  />
                  <Button type="submit" size="icon" className="h-9 w-9" aria-label="Send" disabled={!conversationId || sending || !draft.trim()}>
                    <Send className="h-4 w-4" />
                  </Button>
                </form>
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

function VideoPane({ peer, camOff, muted, onToggleCam, onToggleMute, onEnd }: { peer: CommsPeer; camOff: boolean; muted: boolean; onToggleCam: () => void; onToggleMute: () => void; onEnd: () => void }) {
  return (
    <div className="relative flex flex-1 flex-col bg-[oklch(0.18_0.03_260)] text-white">
      <div className="relative flex flex-1 items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,oklch(0.35_0.09_260)_0%,transparent_60%),radial-gradient(circle_at_70%_80%,oklch(0.3_0.08_200)_0%,transparent_55%)]" />
        <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-white/10 text-3xl font-bold backdrop-blur">
          {peer.initials}
        </div>
        {/* Self-view */}
        <div className="absolute bottom-3 right-3 flex h-20 w-28 items-center justify-center rounded-lg border border-white/20 bg-black/40 text-[10px] uppercase tracking-wider text-white/70 backdrop-blur">
          {camOff ? "Camera off" : "You"}
        </div>
        <div className="absolute left-3 top-3 rounded-full bg-black/40 px-2 py-0.5 text-[10px] font-medium backdrop-blur">
          <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-success animate-pulse" /> Live
        </div>
      </div>
      <div className="flex items-center justify-center gap-2 border-t border-white/10 bg-black/30 p-3">
        <CallBtn onClick={onToggleMute} active={muted} label={muted ? "Unmute" : "Mute"}>
          {muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
        </CallBtn>
        <CallBtn onClick={onToggleCam} active={camOff} label={camOff ? "Camera on" : "Camera off"}>
          {camOff ? <VideoOff className="h-4 w-4" /> : <Video className="h-4 w-4" />}
        </CallBtn>
        <CallBtn onClick={onEnd} danger label="End">
          <PhoneOff className="h-4 w-4" />
        </CallBtn>
      </div>
    </div>
  );
}

function VoicePane({ peer, muted, onToggleMute, onEnd, elapsed }: { peer: CommsPeer; muted: boolean; onToggleMute: () => void; onEnd: () => void; elapsed: string }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-between bg-gradient-to-b from-surface-muted to-surface p-6">
      <div className="flex flex-1 flex-col items-center justify-center gap-3">
        <div className="relative">
          <div className="absolute -inset-3 animate-ping rounded-full bg-primary/20" />
          <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-primary text-2xl font-bold text-primary-foreground">
            {peer.initials}
          </div>
        </div>
        <div className="text-center">
          <div className="font-semibold">{peer.name}</div>
          <div className="text-xs text-muted-foreground">Voice call · {elapsed}</div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <CallBtn onClick={onToggleMute} active={muted} label={muted ? "Unmute" : "Mute"}>
          {muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
        </CallBtn>
        <CallBtn label="Speaker">
          <Volume2 className="h-4 w-4" />
        </CallBtn>
        <CallBtn onClick={onEnd} danger label="End">
          <PhoneOff className="h-4 w-4" />
        </CallBtn>
      </div>
    </div>
  );
}

function CallBtn({ children, onClick, active, danger, label }: { children: React.ReactNode; onClick?: () => void; active?: boolean; danger?: boolean; label: string }) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cn(
        "flex h-10 w-10 items-center justify-center rounded-full border transition-colors",
        danger
          ? "border-destructive bg-destructive text-destructive-foreground hover:bg-destructive/90"
          : active
            ? "border-warning/40 bg-warning/20 text-warning-foreground"
            : "border-border bg-surface text-foreground hover:bg-muted"
      )}
    >
      {children}
    </button>
  );
}
